/* Phase 69 — Premium "Vehicle Condition & Trust" section for listing detail.
 *
 * Renders Phase 68 dealer-grade attributes (accident-free, MFK, service book,
 * warranty, battery health, key count, etc.) as a multilingual, mobile-first,
 * conditionally-rendered grid of premium cards and pill badges.
 *
 * Returns null when no trust fields are set — never an empty section.
 */
import React from "react";
import {
  ShieldCheck, Cigarette, BookCheck, BadgeCheck, FileCheck2, Disc3, KeyRound,
  BatteryCharging, BatteryFull, BatteryLow, BatteryMedium, CalendarCheck, Wrench,
} from "lucide-react";

/* ─── locale strings (DE / FR / IT / EN) ───────────────────────────── */
const STRINGS = {
  de: {
    heading: "Zustand & Vertrauen",
    sub: "Vom Verkäufer bestätigte Eigenschaften",
    trust: {
      accident_free: "Unfallfrei",
      non_smoker: "Nichtraucherfahrzeug",
      service_book: "Service-Heft gepflegt",
      warranty_available: "Garantie vorhanden",
      ab_mfk: "Frisch ab MFK",
      new_tires: "Neue Reifen",
    },
    facts_title: "Geprüfte Fakten",
    last_mfk: "Letzte MFK",
    last_service: "Letzter Service",
    warranty_months: (n) => `${n} ${n === 1 ? "Monat" : "Monate"} Garantie`,
    keys: "Schlüssel",
    keys_count: (n) => `${n} ${n === 1 ? "Schlüssel" : "Schlüssel"}`,
    battery: {
      title: "Batteriegesundheit",
      excellent: "Ausgezeichneter Zustand",
      good: "Guter Zustand",
      fair: "Akzeptabel",
      check: "Prüfung empfohlen",
    },
  },
  fr: {
    heading: "Condition & confiance",
    sub: "Caractéristiques confirmées par le vendeur",
    trust: {
      accident_free: "Sans accident",
      non_smoker: "Véhicule non-fumeur",
      service_book: "Carnet d'entretien suivi",
      warranty_available: "Garantie disponible",
      ab_mfk: "Récent contrôle OFROU",
      new_tires: "Pneus neufs",
    },
    facts_title: "Faits vérifiés",
    last_mfk: "Dernier contrôle OFROU",
    last_service: "Dernier entretien",
    warranty_months: (n) => `${n} mois de garantie`,
    keys: "Clés",
    keys_count: (n) => `${n} ${n === 1 ? "clé" : "clés"}`,
    battery: {
      title: "Santé de la batterie",
      excellent: "Excellent état",
      good: "Bon état",
      fair: "Acceptable",
      check: "Vérification recommandée",
    },
  },
  it: {
    heading: "Condizione & fiducia",
    sub: "Caratteristiche confermate dal venditore",
    trust: {
      accident_free: "Senza incidenti",
      non_smoker: "Veicolo non fumatore",
      service_book: "Libretto tagliandi completo",
      warranty_available: "Garanzia disponibile",
      ab_mfk: "Da collaudo recente",
      new_tires: "Pneumatici nuovi",
    },
    facts_title: "Fatti verificati",
    last_mfk: "Ultimo collaudo",
    last_service: "Ultimo tagliando",
    warranty_months: (n) => `${n} ${n === 1 ? "mese" : "mesi"} di garanzia`,
    keys: "Chiavi",
    keys_count: (n) => `${n} ${n === 1 ? "chiave" : "chiavi"}`,
    battery: {
      title: "Salute della batteria",
      excellent: "Stato eccellente",
      good: "Buono stato",
      fair: "Accettabile",
      check: "Controllo consigliato",
    },
  },
  en: {
    heading: "Condition & trust",
    sub: "Seller-confirmed vehicle attributes",
    trust: {
      accident_free: "Accident-free",
      non_smoker: "Non-smoker vehicle",
      service_book: "Full service history",
      warranty_available: "Warranty available",
      ab_mfk: "Fresh roadworthy cert.",
      new_tires: "New tyres",
    },
    facts_title: "Verified facts",
    last_mfk: "Last roadworthy check",
    last_service: "Last service",
    warranty_months: (n) => `${n} ${n === 1 ? "month" : "months"} warranty`,
    keys: "Keys",
    keys_count: (n) => `${n} ${n === 1 ? "key" : "keys"}`,
    battery: {
      title: "Battery health",
      excellent: "Excellent condition",
      good: "Good condition",
      fair: "Acceptable",
      check: "Inspection recommended",
    },
  },
};

const T = (locale) => STRINGS[locale] || STRINGS.de;

/* ─── helpers ─────────────────────────────────────────────────────── */
const fmtDate = (iso, locale = "de") => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const localeMap = { de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-CH" };
    return d.toLocaleDateString(localeMap[locale] || "de-CH", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
};

const isEv = (car) => {
  const f = (car?.fuel || "").toLowerCase();
  return f === "elektro" || f === "electric" || f === "hybrid";
};

/* Battery tier: returns {color, bg, ring, icon, label-key} */
const batteryTier = (pct, t) => {
  const p = Number(pct) || 0;
  if (p >= 90) return { tone: "excellent", color: "text-emerald-300", bg: "from-emerald-500/15 to-emerald-500/0", ring: "ring-emerald-500/40", track: "bg-emerald-500", icon: BatteryFull, label: t.battery.excellent };
  if (p >= 75) return { tone: "good", color: "text-emerald-200", bg: "from-emerald-500/10 to-emerald-500/0", ring: "ring-emerald-500/30", track: "bg-emerald-400", icon: BatteryCharging, label: t.battery.good };
  if (p >= 60) return { tone: "fair", color: "text-amber-300", bg: "from-amber-500/10 to-amber-500/0", ring: "ring-amber-500/30", track: "bg-amber-400", icon: BatteryMedium, label: t.battery.fair };
  return { tone: "check", color: "text-orange-300", bg: "from-orange-500/10 to-orange-500/0", ring: "ring-orange-500/30", track: "bg-orange-400", icon: BatteryLow, label: t.battery.check };
};

/* ─── component ───────────────────────────────────────────────────── */
export default function TrustSection({ car, locale = "de" }) {
  if (!car) return null;
  const t = T(locale);

  // ── 1. Boolean badges (only when explicitly true) ─────────────────
  const trustBadges = [
    { key: "accident_free",      icon: ShieldCheck, label: t.trust.accident_free,      show: car.accident_free === true },
    { key: "non_smoker",         icon: Cigarette,   label: t.trust.non_smoker,         show: car.non_smoker === true },
    { key: "service_book",       icon: BookCheck,   label: t.trust.service_book,       show: car.service_book === true },
    { key: "warranty_available", icon: BadgeCheck,  label: t.trust.warranty_available, show: car.warranty_available === true },
    { key: "ab_mfk",             icon: FileCheck2,  label: t.trust.ab_mfk,             show: !!car.ab_mfk },
    { key: "new_tires",          icon: Disc3,       label: t.trust.new_tires,          show: !!car.new_tires },
  ].filter((b) => b.show);

  // ── 2. Numeric / date facts ───────────────────────────────────────
  const facts = [];
  if (car.mfk_valid_until) {
    facts.push({ key: "mfk", icon: CalendarCheck, label: t.last_mfk, value: fmtDate(car.mfk_valid_until, locale) });
  }
  if (car.last_service_date) {
    facts.push({ key: "service", icon: Wrench, label: t.last_service, value: fmtDate(car.last_service_date, locale) });
  }
  if (Number(car.warranty_months) > 0) {
    facts.push({ key: "warranty", icon: BadgeCheck, label: t.trust.warranty_available, value: t.warranty_months(Number(car.warranty_months)) });
  }
  if (Number(car.number_of_keys) > 0) {
    facts.push({ key: "keys", icon: KeyRound, label: t.keys, value: t.keys_count(Number(car.number_of_keys)) });
  }

  // ── 3. EV battery (separate prominent card) ──────────────────────
  const showBattery = isEv(car) && Number(car.battery_health_pct) > 0;
  const battery = showBattery ? batteryTier(car.battery_health_pct, t) : null;

  // ── Early return if absolutely nothing to show ───────────────────
  if (trustBadges.length === 0 && facts.length === 0 && !showBattery) return null;

  return (
    <section
      className="mt-12 rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-5 sm:p-7"
      data-testid="trust-section"
      aria-labelledby="trust-section-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="trust-section-heading" className="font-display text-xl font-semibold text-white sm:text-2xl">
            {t.heading}
          </h2>
          <p className="mt-1 text-sm text-zinc-400">{t.sub}</p>
        </div>
        {trustBadges.length > 0 && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300"
            data-testid="trust-positive-count"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {trustBadges.length}/6
          </span>
        )}
      </div>

      {/* ─ Trust pill grid ─ */}
      {trustBadges.length > 0 && (
        <div
          className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="trust-badges-grid"
        >
          {trustBadges.map(({ key, icon: Icon, label }) => (
            <div
              key={key}
              data-testid={`trust-badge-${key}`}
              className="group relative flex items-center gap-3 overflow-hidden rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.07] to-transparent px-4 py-3 transition-colors hover:border-emerald-400/40"
            >
              <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30">
                <Icon className="h-4 w-4 text-emerald-300" />
              </span>
              <span className="text-sm font-medium leading-tight text-white">{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ─ Verified facts grid ─ */}
      {facts.length > 0 && (
        <div className="mt-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
            {t.facts_title}
          </p>
          <div
            className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4"
            data-testid="trust-facts-grid"
          >
            {facts.map(({ key, icon: Icon, label, value }) => (
              <div
                key={key}
                data-testid={`trust-fact-${key}`}
                className="rounded-lg border border-zinc-800 bg-black/40 p-3.5"
              >
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                  <Icon className="h-3 w-3 text-red-500" /> {label}
                </div>
                <p className="mt-1.5 font-display text-base font-semibold text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─ EV Battery card ─ */}
      {showBattery && battery && (
        <BatteryCard pct={car.battery_health_pct} battery={battery} title={t.battery.title} />
      )}
    </section>
  );
}

/* ─── EV battery card ─────────────────────────────────────────────── */
function BatteryCard({ pct, battery, title }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  const Icon = battery.icon;
  return (
    <div
      className={`mt-6 overflow-hidden rounded-2xl bg-gradient-to-br ${battery.bg} ring-1 ${battery.ring}`}
      data-testid="trust-battery-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 sm:p-6">
        <div className="flex min-w-0 items-center gap-4">
          <span className={`grid h-12 w-12 flex-shrink-0 place-items-center rounded-full bg-black/40 ring-2 ${battery.ring}`}>
            <Icon className={`h-6 w-6 ${battery.color}`} />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
              {title}
            </p>
            <p className="mt-0.5 font-display text-2xl font-bold leading-tight text-white sm:text-3xl">
              <span data-testid="trust-battery-pct">{p}%</span>
              <span className="ml-2 align-middle text-xs font-medium uppercase tracking-wider text-zinc-400">SOH</span>
            </p>
            <p className={`mt-1 text-sm font-medium ${battery.color}`} data-testid="trust-battery-label">
              {battery.label}
            </p>
          </div>
        </div>
      </div>
      {/* Progress bar */}
      <div className="px-5 pb-5 sm:px-6 sm:pb-6">
        <div className="h-2 w-full overflow-hidden rounded-full bg-black/40 ring-1 ring-white/5">
          <div
            className={`h-full rounded-full ${battery.track} transition-[width] duration-500`}
            style={{ width: `${p}%` }}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={p}
            data-testid="trust-battery-bar"
          />
        </div>
        <div className="mt-1.5 flex justify-between text-[10px] uppercase tracking-wider text-zinc-600">
          <span>0%</span><span>50%</span><span>100%</span>
        </div>
      </div>
    </div>
  );
}
